// src/pages/ChatBot/query-logs/header-filter.tsx
//
// Funnel filter controls that live inside the Queries table header. Each
// Status/Flags <th> hosts a borderless funnel button; clicking it opens a
// popover of filter entries. The popover is position: fixed and anchored from
// the button's getBoundingClientRect() so it escapes the table card's
// overflow-x: auto clip — an absolutely-positioned child of the <th> would be
// cropped by that scroll container. It closes on click-outside, Escape, and
// on window scroll/resize (the anchor would otherwise drift).
//
// Status is single-select (radio behaviour, closes on pick); Flags is
// multi-select (stays open on toggle, closes on outside click / Escape).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import type { QueryFlag, QueryFlagName, QueryStatus, QueryStatusName } from './types';

// Funnel glyph sized to sit inline with the ~0.9rem header text.
const FUNNEL_ICON_SIZE = 13;
// Gap between the funnel button's bottom edge and the popover's top edge.
const POPOVER_GAP = 4;
// Keep the popover this far from the viewport's right edge when clamping.
const VIEWPORT_MARGIN = 8;

// Standard funnel: a wide top edge whose sides converge to a short stem.
// fill: currentColor so the button's CSS colour drives the active tint.
function FunnelIcon() {
  return (
    <svg
      width={FUNNEL_ICON_SIZE}
      height={FUNNEL_ICON_SIZE}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 3h12l-4.5 6v4h-3V9L2 3z" />
    </svg>
  );
}

interface PopoverPosition {
  top: number;
  left: number;
}

interface HeaderFilterShellProps {
  ariaLabel: string;
  isActive: boolean;
  badgeCount?: number;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: ReactNode;
}

// Owns the funnel button plus the fixed-positioned popover shell: anchoring,
// viewport clamping, click-outside, Escape, and scroll/resize dismissal. The
// caller (the panel) decides which single filter is open, so only one popover
// is ever mounted at a time.
function HeaderFilterShell({
  ariaLabel,
  isActive,
  badgeCount,
  isOpen,
  onToggle,
  onClose,
  children,
}: HeaderFilterShellProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  // Anchor the popover below the funnel, left-aligned to it, clamped to the
  // viewport's right edge. Runs after the popover is in the DOM (so its width
  // is measurable) but before paint, so there is no visible jump.
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setPosition(null);
      return;
    }
    const anchor = buttonRef.current.getBoundingClientRect();
    const popoverWidth = popoverRef.current?.offsetWidth ?? 0;
    const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, maxLeft));
    setPosition({ top: anchor.bottom + POPOVER_GAP, left });
  }, [isOpen]);

  // Click-outside + dismiss on scroll/resize (fixed anchoring can't follow a
  // moving button, so the simplest correct behaviour is to close).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The funnel button toggles itself on click — ignore it here.
      if (buttonRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const handleReposition = () => onClose();
    document.addEventListener('mousedown', handleMouseDown);
    // Capture phase so a scroll inside the overflow-x table card is caught too.
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, onClose]);

  useEscapeClose(onClose, isOpen);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`qlog-hf-funnel${isActive ? ' qlog-hf-funnel-active' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <FunnelIcon />
        {badgeCount ? <sup className="qlog-hf-badge">{badgeCount}</sup> : null}
      </button>
      {isOpen && (
        <div
          ref={popoverRef}
          className="qlog-hf-popover"
          // Hidden until measured so the first (unclamped) paint never shows.
          style={
            position
              ? { top: position.top, left: position.left }
              : { top: 0, left: 0, visibility: 'hidden' }
          }
        >
          {children}
        </div>
      )}
    </>
  );
}

interface StatusHeaderFilterProps {
  options: QueryStatus[];
  value: QueryStatusName | '';
  onChange: (value: QueryStatusName | '') => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

// Single-select status funnel: an "All Statuses" reset plus one entry per
// catalog status. Picking an entry applies and closes.
export function StatusHeaderFilter({
  options,
  value,
  onChange,
  isOpen,
  onToggle,
  onClose,
}: StatusHeaderFilterProps) {
  const handleSelect = (next: QueryStatusName | '') => {
    onChange(next);
    onClose();
  };

  return (
    <HeaderFilterShell
      ariaLabel="Filter by status"
      isActive={value !== ''}
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
    >
      <ul className="qlog-hf-list" role="menu" aria-label="Status filter">
        <StatusEntry
          label="All Statuses"
          selected={value === ''}
          onSelect={() => handleSelect('')}
        />
        {options.map((option) => (
          <StatusEntry
            key={option.id}
            label={option.status}
            selected={value === option.status}
            onSelect={() => handleSelect(option.status)}
          />
        ))}
      </ul>
    </HeaderFilterShell>
  );
}

interface StatusEntryProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
}

function StatusEntry({ label, selected, onSelect }: StatusEntryProps) {
  return (
    <li role="none">
      <button
        type="button"
        role="menuitemradio"
        aria-checked={selected}
        className={`qlog-hf-entry${selected ? ' qlog-hf-entry-selected' : ''}`}
        onClick={onSelect}
      >
        <span className="qlog-hf-check" aria-hidden="true">
          {selected ? '✓' : ''}
        </span>
        {label}
      </button>
    </li>
  );
}

interface FlagsHeaderFilterProps {
  options: QueryFlag[];
  selected: QueryFlagName[];
  onToggleFlag: (flag: QueryFlagName) => void;
  onClear: () => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

// Multi-select flag funnel: a checkbox per catalog flag. Toggling applies
// immediately but keeps the popover open; a Clear action appears once one or
// more flags are selected.
export function FlagsHeaderFilter({
  options,
  selected,
  onToggleFlag,
  onClear,
  isOpen,
  onToggle,
  onClose,
}: FlagsHeaderFilterProps) {
  return (
    <HeaderFilterShell
      ariaLabel="Filter by flags"
      isActive={selected.length > 0}
      badgeCount={selected.length}
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
    >
      <div className="qlog-hf-list" role="group" aria-label="Flag filters">
        {options.map((option) => {
          const isChecked = selected.includes(option.flag);
          return (
            <label
              key={option.id}
              className={`qlog-hf-check-row${isChecked ? ' qlog-hf-check-row-on' : ''}`}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggleFlag(option.flag)}
              />
              <span>{option.flag}</span>
            </label>
          );
        })}
        {selected.length > 0 && (
          <button type="button" className="qlog-hf-clear" onClick={onClear}>
            Clear
          </button>
        )}
      </div>
    </HeaderFilterShell>
  );
}
